import React, { useState, useEffect } from 'react';
import { 
  Mail, Phone, MapPin, Download, Briefcase, GraduationCap, 
  Globe, Award, ChevronRight, Palette, Video, Camera, Film,
  Layout, TrendingUp, BarChart3, FileText, PieChart, Languages,
  Folder, CheckCircle, Target, ChevronLeft, ExternalLink, Linkedin
} from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { cvData } from '../data/mockData';

const CVWebsite = () => {
  const [activeSection, setActiveSection] = useState('hero');
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const totalScroll = document.documentElement.scrollHeight - window.innerHeight;
      const currentScroll = window.scrollY;
      setScrollProgress((currentScroll / totalScroll) * 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const iconMap = {
    Palette, Video, Camera, Film, Globe, Layout, TrendingUp, BarChart3, FileText, PieChart
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const downloadCV = () => {
    const link = document.createElement('a');
    link.href = '/cv.pdf';
    link.download = 'Erika_Morishita_CV.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Carousel component for gallery
  const ImageCarousel = ({ images, title }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    const nextSlide = () => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    };

    const prevSlide = () => {
      setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    return (
      <div className="relative w-full h-96 bg-gray-100 rounded-lg overflow-hidden">
        <img 
          src={images[currentIndex]} 
          alt={`${title} gallery ${currentIndex + 1}`}
          className="w-full h-full object-contain"
        />
        
        {/* Navigation Buttons */}
        {images.length > 1 && (
          <>
            <button
              onClick={prevSlide}
              className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all"
              style={{ zIndex: 10 }}
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-3 rounded-full transition-all"
              style={{ zIndex: 10 }}
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            {/* Indicators */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {images.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    idx === currentIndex ? 'bg-white w-8' : 'bg-white/50'
                  }`}
                  style={{ backgroundColor: idx === currentIndex ? '#00A8E1' : 'rgba(255, 255, 255, 0.5)' }}
                />
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white text-black">
      {/* Progress Bar */}
      <div className="fixed top-0 left-0 w-full h-1 bg-gray-200 z-50">
        <div 
          className="h-full transition-all duration-300"
          style={{ width: `${scrollProgress}%`, background: '#00A8E1' }}
        />
      </div>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-gray-200 z-40 mt-1">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <img 
              src="/assets/logo.png" 
              alt="Erika Morishita Logo" 
              className="h-12 w-auto cursor-pointer"
              onClick={() => scrollToSection('hero')}
            />
            <div className="hidden md:flex items-center gap-6">
              {['About', 'Experience', 'Case Studies', 'Skills', 'Education', 'Contact'].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase().replace(' ', '-'))}
                  className="text-sm font-medium hover:text-[#00A8E1] transition-colors"
                >
                  {item}
                </button>
              ))}
              <Button 
                onClick={downloadCV}
                variant="default" 
                style={{ background: '#00A8E1' }}
                className="text-white hover:opacity-90 ml-4"
              >
                <Download className="w-4 h-4 mr-2" />
                Download CV
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="hero" className="pt-32 pb-20 px-6 text-white" style={{ background: '#1a1a1a' }}>
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl">
            <div className="mb-8">
              <img 
                src="/assets/logo.png" 
                alt="Erika Morishita Logo" 
                className="h-32 w-auto mb-6"
              />
            </div>
            <p className="text-2xl md:text-3xl font-light mb-8 max-w-3xl" style={{ color: '#888' }}>
              {cvData.personal.title}
            </p>
            <div className="flex flex-wrap gap-6 mb-12" style={{ color: '#888' }}>
              <div className="flex items-center gap-2">
                <MapPin className="w-5 h-5" style={{ color: '#00A8E1' }} />
                <span>{cvData.personal.location}</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-5 h-5" style={{ color: '#00A8E1' }} />
                <span>{cvData.personal.email}</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-5 h-5" style={{ color: '#00A8E1' }} />
                <span>{cvData.personal.phone}</span>
              </div>
            </div>
            <Button 
              onClick={() => scrollToSection('about')}
              style={{ background: '#00A8E1' }}
              className="text-white hover:opacity-90"
            >
              Explore My Work
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-5xl font-black mb-6 leading-tight">
                Transforming Complex Ideas Into Compelling Stories
              </h2>
              <div className="w-20 h-1 mb-8" style={{ background: '#00A8E1' }}></div>
            </div>
            <div>
              <p className="text-lg leading-relaxed text-gray-700">
                {cvData.personal.summary}
              </p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-20">
            <div className="text-center p-6 border border-gray-200 hover:border-[#00A8E1] transition-colors">
              <div className="text-4xl font-black mb-2" style={{ color: '#00A8E1' }}>13+</div>
              <div className="text-sm uppercase tracking-wider text-gray-600">Years Experience</div>
            </div>
            <div className="text-center p-6 border border-gray-200 hover:border-[#00A8E1] transition-colors">
              <div className="text-4xl font-black mb-2" style={{ color: '#00A8E1' }}>200+</div>
              <div className="text-sm uppercase tracking-wider text-gray-600">Multimedia Products</div>
            </div>
            <div className="text-center p-6 border border-gray-200 hover:border-[#00A8E1] transition-colors">
              <div className="text-4xl font-black mb-2" style={{ color: '#00A8E1' }}>30+</div>
              <div className="text-sm uppercase tracking-wider text-gray-600">Offices Trained</div>
            </div>
            <div className="text-center p-6 border border-gray-200 hover:border-[#00A8E1] transition-colors">
              <div className="text-4xl font-black mb-2" style={{ color: '#00A8E1' }}>3</div>
              <div className="text-sm uppercase tracking-wider text-gray-600">Languages</div>
            </div>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section id="experience" className="py-24 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <div className="flex items-center gap-4 mb-4">
              <Briefcase className="w-8 h-8" style={{ color: '#00A8E1' }} />
              <h2 className="text-5xl font-black">Experience</h2>
            </div>
            <div className="w-20 h-1" style={{ background: '#00A8E1' }}></div>
          </div>

          <div className="space-y-12">
            {cvData.experience.map((exp, index) => (
              <Card key={exp.id} className="p-8 border-2 border-gray-200 hover:border-[#00A8E1] transition-all duration-300 hover:shadow-xl bg-white">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-6">
                  <div className="mb-4 md:mb-0">
                    <h3 className="text-2xl font-bold mb-2">{exp.title}</h3>
                    <p className="text-lg font-medium" style={{ color: '#888' }}>{exp.organization}</p>
                  </div>
                  <div className="text-sm font-semibold text-white px-4 py-2 rounded inline-block" style={{ background: '#00A8E1' }}>
                    {exp.duration}
                  </div>
                </div>
                <ul className="space-y-3">
                  {exp.responsibilities.map((resp, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <ChevronRight className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: '#00A8E1' }} />
                      <span className="text-gray-700 leading-relaxed">{resp}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Section */}
      <section id="case-studies" className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <div className="flex items-center gap-4 mb-4">
              <Folder className="w-8 h-8" style={{ color: '#00A8E1' }} />
              <h2 className="text-5xl font-black">Case Studies</h2>
            </div>
            <div className="w-20 h-1" style={{ background: '#00A8E1' }}></div>
            <p className="text-lg text-gray-600 mt-6 max-w-3xl">
              Proven track record of transforming complex challenges into impactful solutions across international organizations
            </p>
          </div>

          <div className="space-y-12">
            {cvData.caseStudies.map((study, index) => (
              <Card 
                key={study.id} 
                className="overflow-hidden border-2 border-gray-200 hover:border-[#00A8E1] transition-all duration-300 hover:shadow-2xl"
              >
                <div className="p-8">
                  {/* Header */}
                  <div className="mb-6">
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                      <span 
                        className="text-xs font-bold px-3 py-1 rounded-full text-white"
                        style={{ background: '#00A8E1' }}
                      >
                        {study.category}
                      </span>
                      <span className="text-sm font-medium" style={{ color: '#888' }}>
                        {study.organization}
                      </span>
                    </div>
                    <h3 className="text-3xl font-bold mb-3">{study.title}</h3>
                    <p className="text-lg text-gray-700 leading-relaxed">{study.description}</p>
                  </div>

                  {/* Content Grid */}
                  <div className="grid md:grid-cols-2 gap-8">
                    {/* Responsibilities */}
                    <div>
                      <div className="flex items-center gap-2 mb-4">
                        <CheckCircle className="w-5 h-5" style={{ color: '#00A8E1' }} />
                        <h4 className="font-bold text-lg">Key Responsibilities</h4>
                      </div>
                      <ul className="space-y-2">
                        {study.responsibilities.map((resp, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-gray-700">
                            <span className="text-[#00A8E1] mt-1">•</span>
                            <span className="text-sm">{resp}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Achievements */}
                    <div>
                      <div className="flex items-center gap-2 mb-4">
                        <Target className="w-5 h-5" style={{ color: '#00A8E1' }} />
                        <h4 className="font-bold text-lg">Achievements</h4>
                      </div>
                      <ul className="space-y-2">
                        {study.achievements.map((achievement, idx) => {
                          // Check if this achievement has a link
                          const hasLink = study.achievementLinks && 
                            Object.keys(study.achievementLinks).some(key => achievement.includes(key));
                          const linkKey = hasLink ? Object.keys(study.achievementLinks).find(key => achievement.includes(key)) : null;
                          const linkUrl = linkKey ? study.achievementLinks[linkKey] : null;
                          
                          return (
                            <li key={idx} className="flex items-start gap-2 text-gray-700">
                              <span className="text-[#00A8E1] mt-1">•</span>
                              <span className="text-sm flex-1">
                                {achievement}
                                {linkUrl && (
                                  <a 
                                    href={linkUrl} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 ml-2 text-[#00A8E1] hover:underline"
                                  >
                                    <ExternalLink className="w-3 h-3" />
                                  </a>
                                )}
                              </span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>

                  {/* Gallery Carousel */}
                  {study.gallery && study.gallery.length > 0 && (
                    <div className="mt-8">
                      <h4 className="font-bold text-sm uppercase tracking-wider text-gray-600 mb-4">
                        Project Gallery
                      </h4>
                      <ImageCarousel images={study.gallery} title={study.title} />
                    </div>
                  )}

                  {/* Deliverables */}
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h4 className="font-bold text-sm uppercase tracking-wider text-gray-600 mb-3">
                      Key Deliverables
                    </h4>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {study.deliverables.map((deliverable, idx) => (
                        <span 
                          key={idx}
                          className="text-xs px-3 py-1 bg-gray-100 border border-gray-300 rounded-full text-gray-700"
                        >
                          {deliverable}
                        </span>
                      ))}
                    </div>
                    {study.videoPortfolioUrl && (
                      <a 
                        href={study.videoPortfolioUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-block"
                      >
                        <Button 
                          style={{ background: '#00A8E1' }}
                          className="text-white hover:opacity-90 mt-2"
                        >
                          <Video className="w-4 h-4 mr-2" />
                          View Video Portfolio
                        </Button>
                      </a>
                    )}
                  </div>

                  {/* Impact Banner */}
                  <div className="mt-6 p-4 rounded-lg" style={{ background: 'rgba(0, 168, 225, 0.1)' }}>
                    <div className="flex items-start gap-3">
                      <Award className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#00A8E1' }} />
                      <div>
                        <p className="font-semibold text-sm text-gray-900 mb-1">Impact</p>
                        <p className="text-sm text-gray-700">{study.impact}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="py-24 px-6 bg-black text-white">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <h2 className="text-5xl font-black mb-4">Core Skills</h2>
            <div className="w-20 h-1 bg-white"></div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 mb-20">
            {cvData.skills.map((skill, index) => {
              const IconComponent = iconMap[skill.icon];
              return (
                <div 
                  key={index}
                  className="flex flex-col items-center text-center p-6 border border-gray-700 hover:border-[#00A8E1] transition-all duration-300 hover:bg-gray-900"
                >
                  {IconComponent && <IconComponent className="w-10 h-10 mb-4" style={{ color: '#00A8E1' }} />}
                  <span className="text-sm font-medium">{skill.name}</span>
                </div>
              );
            })}
          </div>

          {/* Software Proficiency */}
          <div>
            <h3 className="text-3xl font-bold mb-8">Software Proficiency</h3>
            <div className="grid md:grid-cols-2 gap-6">
              {cvData.software.map((soft, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">{soft.name}</span>
                    <span className="text-sm text-gray-400">{soft.proficiency}%</span>
                  </div>
                  <div className="w-full bg-gray-700 h-2 rounded-full overflow-hidden">
                    <div 
                      className="h-full transition-all duration-1000"
                      style={{ width: `${soft.proficiency}%`, background: '#00A8E1' }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Education Section */}
      <section id="education" className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <div className="flex items-center gap-4 mb-4">
              <GraduationCap className="w-8 h-8" style={{ color: '#00A8E1' }} />
              <h2 className="text-5xl font-black">Education</h2>
            </div>
            <div className="w-20 h-1" style={{ background: '#00A8E1' }}></div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {cvData.education.map((edu) => (
              <Card key={edu.id} className="p-8 border-2 border-gray-200 hover:border-[#00A8E1] transition-all bg-gray-50">
                <div className="mb-4">
                  <h3 className="text-xl font-bold mb-2">{edu.degree}</h3>
                  {edu.achievement && (
                    <div className="inline-block text-white text-xs font-semibold px-3 py-1 rounded mb-3" style={{ background: '#00A8E1' }}>
                      {edu.achievement}
                    </div>
                  )}
                  <p className="text-lg font-semibold text-gray-700">{edu.institution}</p>
                  <p className="text-gray-600">{edu.location}</p>
                </div>
                <div className="text-sm font-medium text-gray-500">{edu.duration}</div>
                {edu.award && (
                  <div className="mt-4 pt-4 border-t border-gray-300">
                    <div className="flex items-start gap-2">
                      <Award className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#00A8E1' }} />
                      <span className="text-sm text-gray-700">{edu.award}</span>
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>

          {/* Languages */}
          <div className="mt-16">
            <div className="flex items-center gap-3 mb-6">
              <Languages className="w-6 h-6" style={{ color: '#00A8E1' }} />
              <h3 className="text-3xl font-bold">Languages</h3>
            </div>
            <div className="grid grid-cols-3 gap-6">
              {cvData.languages.map((lang, index) => (
                <div key={index} className="p-6 border-2 border-gray-200 hover:border-[#00A8E1] transition-colors text-center">
                  <div className="text-xl font-bold mb-1">{lang.name}</div>
                  <div className="text-sm text-gray-600">{lang.level}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Hire Me Section */}
      <section className="py-24 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <h2 className="text-5xl font-black mb-4">Why Hire Me</h2>
            <div className="w-20 h-1" style={{ background: '#00A8E1' }}></div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {cvData.whyHireMe.map((reason, index) => {
              const icons = [
                <Award className="w-6 h-6 text-white" />,
                <Palette className="w-6 h-6 text-white" />,
                <TrendingUp className="w-6 h-6 text-white" />,
                <CheckCircle className="w-6 h-6 text-white" />,
                <Briefcase className="w-6 h-6 text-white" />,
                <Target className="w-6 h-6 text-white" />,
                <Globe className="w-6 h-6 text-white" />,
                <Award className="w-6 h-6 text-white" />
              ];
              
              return (
                <div 
                  key={index}
                  className="p-6 bg-white border-l-4 hover:shadow-lg transition-shadow"
                  style={{ borderLeftColor: '#00A8E1' }}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#00A8E1' }}>
                      {icons[index]}
                    </div>
                    <p className="text-gray-700 leading-relaxed">{reason}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 px-6 bg-black text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-5xl font-black mb-6">Let's Work Together</h2>
          <p className="text-xl mb-12 max-w-2xl mx-auto" style={{ color: '#888' }}>
            Ready to create impactful content that connects and inspires? Get in touch.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
            <Button asChild className="text-white hover:opacity-90 px-8 py-6 text-lg" style={{ background: '#00A8E1' }}>
              <a href="mailto:erikamorishita@gmail.com">
                <Mail className="w-5 h-5 mr-2" />
                Email Me
              </a>
            </Button>
            <Button asChild variant="outline" className="text-white hover:bg-white hover:text-black px-8 py-6 text-lg" style={{ borderColor: '#00A8E1' }}>
              <a href={`tel:${cvData.personal.phone}`}>
                <Phone className="w-5 h-5 mr-2" />
                Call Me
              </a>
            </Button>
          </div>

          <div className="pt-8 border-t border-gray-700" style={{ color: '#888' }}>
            <p>{cvData.personal.email} • {cvData.personal.phone}</p>
            <p className="mt-2">{cvData.personal.location}</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-black text-white border-t border-gray-800">
        <div className="max-w-7xl mx-auto text-center text-gray-500 text-sm">
          <p>© 2025 Erika Morishita. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default CVWebsite;
